export const WORKFLOW_RUN_SEGMENT_JOB_TYPE = 'workflows.run-segment' as const;

/**
 * Wall-clock budget for one `workflows.run-segment` job, in seconds. Single
 * source for the job's own `timeoutSeconds` and for anything that must size a
 * dependency to outlive a run segment (e.g. a MicroVM's
 * `maximumDurationInSeconds` — see `apps/api/src/microvm-browser/service.ts`).
 */
export const WORKFLOW_RUN_SEGMENT_TIMEOUT_SECONDS = 90 * 60;

// Suite Runner Orchestrator Pattern
export const WORKFLOW_TEST_SUITE_ORCHESTRATOR_JOB_TYPE =
  'workflows.test-suite-orchestrator' as const;
export const WORKFLOW_TEST_SUITE_WORKFLOW_JOB_TYPE = 'workflows.test-suite-workflow' as const;
export const WORKFLOW_TEST_SUITE_CREDENTIAL_WARMUP_JOB_TYPE =
  'workflows.test-suite-credential-warmup' as const;
export const WORKFLOW_SUITE_SETUP_JOB_TYPE = 'workflows.suite-setup' as const;
export const WORKFLOW_SEED_JOB_TYPE = 'workflow.seed' as const;
export const WORKFLOW_TEARDOWN_JOB_TYPE = 'workflow.teardown' as const;

/**
 * Maximum wait a seed or teardown flow may declare.
 *
 * These flow types run their waits inline — the executing fiber sleeps in place
 * rather than ending the segment and scheduling a resume job — so an unbounded
 * wait would hold a browser session (and a job slot) open for its whole
 * duration. Standard flows keep the segmented behaviour and are not capped by
 * this.
 */
export const SEED_MAX_WAIT_SECONDS = 300;

/**
 * Flow types whose `wait` nodes sleep inline instead of splitting the run into
 * segments.
 *
 * Seed and teardown flows run as nested sub-runs driven by the parent flow's
 * job. Nothing enqueues a continuation segment for them, so a segmented wait
 * strands the nested run in `waiting` forever. Capped inline sleeps keep them
 * single-segment and always terminal.
 *
 * `setup` is deliberately excluded: inline setup flows strip wait nodes, and
 * suite setups reject them outright.
 */
export function flowTypeUsesInlineWaits(flowType: string | null | undefined): boolean {
  return flowType === 'seed' || flowType === 'teardown';
}

/** A full 40-hex commit id: normalize only SHA-shaped suite pins; push tags stay case-sensitive. */
export const PINNED_COMMIT_PATTERN = /^[0-9a-fA-F]{40}$/;

export type WorkflowSuiteRunPayload = {
  workflowId?: string; // For single-workflow authoring
  /** Filter to specific workflow IDs (e.g. CSV import auto-run) */
  workflowIds?: string[];
  /**
   * Restrict the suite to workflows whose credential references all resolve
   * to this property. When set, login nodes' `credentialTemplateId` /
   * `credentialId` are resolved to their backing property and the suite skips
   * (a) workflows with zero resolvable credentials, and (b) workflows whose
   * credentials resolve to any other property. Null/undefined = no filter.
   */
  propertyId?: string | null;
  /** Target environment for credential resolution and base-URL substitution. */
  environmentId?: string | null;
  /**
   * How the suite picks an environment per flow:
   *  - `'mixed'` (default) — each flow runs against its own preferred env
   *    (`workflows.defaultEnvironmentId`, else the property default). Nothing is
   *    skipped for env-eligibility reasons.
   *  - `'force'` — every flow runs against `environmentId`; flows ineligible for
   *    it are skipped (legacy single-env behavior).
   * Omitted ⇒ `'force'` when an `environmentId` is supplied (back-compat with
   * existing single-env callers), else `'mixed'`.
   */
  environmentMode?: 'mixed' | 'force';
  /**
   * Explicit live sandbox reused from a prior non-successful execution. These
   * fields are internal job plumbing; callers must validate the instance and
   * runtime environment before enqueueing, and the orchestrator validates them
   * again before spawning children.
   */
  retainedSandboxInstanceId?: string | null;
  retainedSandboxRuntimeEnvironmentId?: string | null;
  retainedSandboxTemplateId?: string | null;
  retainedSandboxSourceExecutionId?: string | null;
  triggeredByUserId?: string | null;
  triggeredReason?: string | null;
  /**
   * Filter workflows by tag(s) — any-of semantics. A workflow runs if it has
   * at least one of the supplied tag names. Empty/undefined means "no tag filter".
   */
  tags?: string[] | null;
  /** Filter workflows by name pattern (case-insensitive substring match) */
  namePattern?: string | null;
  /**
   * Clear `degraded` health on every in-scope flow before the run, then run them
   * instead of recording them as "degraded — not run". Equivalent to calling
   * POST /workflows/:id/re-enable on each: health → 'unverified',
   * healthComputedAt → null, healthResetAt → now(). Applies to "run all"/tag
   * scopes and saved suites. Ad-hoc explicit-ID runs remain directly runnable
   * and do not mutate health through this option.
   */
  reEnableDegraded?: boolean;
  /**
   * Run a persisted, user-curated suite. When set, the orchestrator loads the
   * suite's members (ignoring tag/name/id selectors) and runs them in the
   * suite's layer order — layer N+1 spawns only after every workflow in layer N
   * reaches a terminal state.
   */
  suiteId?: string;
  /**
   * "Retry all failed" — reuse an existing terminal execution and retry only
   * its failed/timeout/inconclusive (workflow, scenario) pairs as a mini-suite
   * through the same orchestrator, appending new `attempt` rows against the
   * SAME execution id rather than starting a fresh one. When set, the
   * orchestrator's init phase calls `prepareWorkflowTestSuiteRetry` instead of
   * the normal suite prepare and every other selector field is ignored.
   */
  retryExecutionId?: string;
  /**
   * Optional source attempt ids that narrow a retry to specific
   * (workflow, scenario) buckets. Omitted means every latest retryable bucket.
   */
  retryTestRunIds?: string[];
  /** Internal: selected reruns may target any rerunnable terminal status. */
  retrySelectedTerminal?: boolean;
  /**
   * Internal: resolved suite pins, keyed by lower-cased repository full name
   * for GitHub applications or property id for pushed applications. Release QA
   * seeds the release's commit per repository. Unseeded targets resolve their
   * default version when the suite starts; `pinnedCommit` backstops them.
   */
  sandboxV2CommitByRepo?: Record<string, string>;
  /**
   * The caller's <sha|pushId|tag> pin (`cofactor test-suite run --at`). The run
   * route resolves it for each target's delivery and seeds `sandboxV2CommitByRepo`.
   * Its presence always excludes the run from the default revision, so a
   * pinned run never moves visual baselines.
   */
  pinnedCommit?: string;
  /**
   * Internal (retry in a new orchestrator): the commit the retried execution
   * pinned for each Sandboxes v2 target environment, so a retry runs on the
   * same commit as the attempts it retries.
   */
  sandboxV2PinnedShaByEnvironmentId?: Record<string, string>;
};

/** @deprecated Use WorkflowSuiteRunPayload — kept as alias during in-flight job migration. */
export type WorkflowPropertyTestRunPayload = WorkflowSuiteRunPayload;

export type WorkflowRunSegmentPayload = {
  runId: string;
};

export type WorkflowTestStatus =
  | 'queued'
  | 'running'
  | 'waiting'
  | 'success'
  | 'failed'
  | 'inconclusive'
  | 'skipped'
  | 'flaky_success'
  | 'canceled'
  | 'timeout'
  // Not run because the workflow's health is degraded (suite runs only).
  | 'degraded';

/** Test-run statuses eligible for retry-all. */
export const RETRYABLE_TEST_RUN_STATUSES: ReadonlySet<WorkflowTestStatus> = new Set([
  'failed',
  'timeout',
  'inconclusive',
]);

/** Terminal test-run statuses eligible for an explicit single rerun. */
export const RERUNNABLE_TEST_RUN_STATUSES: ReadonlySet<WorkflowTestStatus> = new Set([
  'success',
  'failed',
  'inconclusive',
  'skipped',
  'flaky_success',
  'canceled',
  'timeout',
]);

/** Hard ceiling for append-only attempts in one workflow/scenario bucket. */
export const MAX_WORKFLOW_TEST_ATTEMPTS = 10;

export type WorkflowTestAttemptLike = {
  workflowId: string;
  status: string;
  scenarioId?: string | null;
  attempt?: number | null;
  createdAt?: Date | string | null;
  layerIndex?: number | null;
};

export type WorkflowTestActiveLayer =
  | { kind: 'none' }
  | { kind: 'unlayered' }
  | { kind: 'layered'; index: number };

/**
 * Return the live layer without collapsing parallel-suite `null` into layer 0.
 * A suite cannot mix layered and unlayered live rows; if malformed data does,
 * the lowest explicit layer wins so later layers cannot start early.
 */
export const getWorkflowTestActiveLayer = (
  runs: ReadonlyArray<Pick<WorkflowTestAttemptLike, 'status' | 'layerIndex'>>
): WorkflowTestActiveLayer => {
  let lowestLayer: number | null = null;
  let hasUnlayered = false;
  for (const run of runs) {
    if (!['queued', 'running', 'waiting'].includes(run.status)) continue;
    if (run.layerIndex === null || run.layerIndex === undefined) {
      hasUnlayered = true;
    } else if (lowestLayer === null || run.layerIndex < lowestLayer) {
      lowestLayer = run.layerIndex;
    }
  }
  if (lowestLayer !== null) return { kind: 'layered', index: lowestLayer };
  return hasUnlayered ? { kind: 'unlayered' } : { kind: 'none' };
};

export const isWorkflowTestRunInActiveLayer = (
  run: Pick<WorkflowTestAttemptLike, 'layerIndex'>,
  activeLayer: WorkflowTestActiveLayer
): boolean =>
  activeLayer.kind === 'unlayered'
    ? run.layerIndex === null || run.layerIndex === undefined
    : activeLayer.kind === 'layered' && run.layerIndex === activeLayer.index;

/** Canonical bucket used by persistence, rollups, retry selection, and UI history. */
export const workflowTestAttemptBucketKey = (
  run: Pick<WorkflowTestAttemptLike, 'workflowId' | 'scenarioId'>
): string => `${run.workflowId}:${run.scenarioId ?? ''}`;

const WORKFLOW_TEST_STATUS_PRIORITY: Readonly<Record<string, number>> = {
  canceled: 0,
  timeout: 1,
  skipped: 2,
  inconclusive: 3,
  failed: 4,
  waiting: 5,
  queued: 6,
  running: 7,
  flaky_success: 8,
  success: 9,
};

const attemptCreatedAt = (value: Date | string | null | undefined): number => {
  if (value instanceof Date) return value.getTime();
  if (typeof value !== 'string') return -Infinity;
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? -Infinity : timestamp;
};

/**
 * Compare rows in one workflow/scenario bucket. Highest attempt wins, then
 * newest creation time, then the legacy status priority for pre-index rows.
 */
export const isNewerWorkflowTestAttempt = <T extends WorkflowTestAttemptLike>(
  candidate: T,
  existing: T
): boolean => {
  const candidateAttempt = candidate.attempt ?? 1;
  const existingAttempt = existing.attempt ?? 1;
  if (candidateAttempt !== existingAttempt) return candidateAttempt > existingAttempt;

  const candidateCreatedAt = attemptCreatedAt(candidate.createdAt);
  const existingCreatedAt = attemptCreatedAt(existing.createdAt);
  if (candidateCreatedAt !== existingCreatedAt) return candidateCreatedAt > existingCreatedAt;

  return (
    (WORKFLOW_TEST_STATUS_PRIORITY[candidate.status] ?? 0) >
    (WORKFLOW_TEST_STATUS_PRIORITY[existing.status] ?? 0)
  );
};

/** Keep the authoritative row for every workflow/scenario attempt bucket. */
export const latestWorkflowTestRunAttempts = <T extends WorkflowTestAttemptLike>(
  runs: readonly T[]
): T[] => {
  const latestByBucket = new Map<string, T>();
  for (const run of runs) {
    const key = workflowTestAttemptBucketKey(run);
    const existing = latestByBucket.get(key);
    if (!existing || isNewerWorkflowTestAttempt(run, existing)) {
      latestByBucket.set(key, run);
    }
  }
  return [...latestByBucket.values()];
};

/**
 * Canonical set of **test-run** statuses that are terminal (no further
 * transitions). Import this instead of re-declaring it — the orchestrator's
 * layer gating and the suite run-detail "layer complete" check share it.
 * NOTE: distinct from terminal *execution* statuses (completed /
 * completed_with_errors / …), which are a separate enum.
 */
export const TERMINAL_TEST_RUN_STATUSES: ReadonlySet<string> = new Set([
  'success',
  'failed',
  'inconclusive',
  'skipped',
  'flaky_success',
  'canceled',
  'timeout',
  'degraded',
]);

export const isTerminalTestRunStatus = (status: string): boolean =>
  TERMINAL_TEST_RUN_STATUSES.has(status);

/**
 * Canonical **test-run** statuses that count as a pass and as a hard failure.
 *
 * These decide what the suite's persisted counters (`successfulWorkflows` /
 * `failedWorkflows`) hold, which is in turn what every reporting surface shows —
 * the runs list, the suite detail page, and the Slack suite card. Import them
 * instead of re-declaring: two surfaces having their own copy of this mapping is
 * exactly how Slack came to count `canceled` as skipped while the counters
 * counted it as failed.
 *
 * Every other terminal status (`skipped`, `degraded`, `inconclusive`) is neither
 * a pass nor a hard failure and is tracked in its own counter column.
 */
export const WORKFLOW_TEST_SUCCESS_STATUSES: ReadonlySet<string> = new Set([
  'success',
  'flaky_success',
]);

export const WORKFLOW_TEST_FAILED_STATUSES: ReadonlySet<string> = new Set([
  'failed',
  'timeout',
  'canceled',
]);

/**
 * Terminal statuses that are neither a pass nor a hard failure — the run
 * produced no product verdict. `skipped` and `degraded` never ran at all;
 * `inconclusive` ran but hit a platform error.
 *
 * Computed by subtraction rather than enumerated, so a status added to
 * `workflow_test_run_status` lands here automatically instead of being silently
 * dropped by a surface that only knows the two named sets above.
 */
export const WORKFLOW_TEST_CONTEXT_STATUSES: ReadonlySet<string> = new Set(
  [...TERMINAL_TEST_RUN_STATUSES].filter(
    (status) =>
      !WORKFLOW_TEST_SUCCESS_STATUSES.has(status) && !WORKFLOW_TEST_FAILED_STATUSES.has(status)
  )
);

/**
 * Workflow reliability dimension, orthogonal to {@link WorkflowDefinitionStatus}
 * (lifecycle). Recomputed after each suite run from the workflow's test-run history.
 *
 * - `unverified`   — no terminal test runs yet
 * - `never_passed` — has run, never a single pass (authoritative via firstPassedAt)
 * - `passing`      — recent window all green, streak below the reliable threshold
 * - `reliable`     — enough consecutive clean passes
 * - `flaky`        — latest run passed, but the recent window had failures / a flaky_success
 * - `failing`      — latest run failed but NOT auto-skipped (< DEGRADE_AFTER failures, or not eligible)
 * - `degraded`     — eligible + enough consecutive failures; skipped in suite runs
 *                    ("degraded — not run") until the workflow is edited or manually re-enabled
 */
export const WORKFLOW_HEALTH = [
  'unverified',
  'never_passed',
  'passing',
  'reliable',
  'flaky',
  'failing',
  'degraded',
] as const;

export type WorkflowHealth = (typeof WORKFLOW_HEALTH)[number];

/** Display label per health state, shared by the web's flow list, filters, and health badges. */
export const WORKFLOW_HEALTH_LABEL: Record<WorkflowHealth, string> = {
  unverified: 'Unverified',
  never_passed: 'Never passed',
  passing: 'Passing',
  reliable: 'Reliable',
  flaky: 'Flaky',
  failing: 'Failing',
  degraded: 'Degraded',
};

/**
 * Management-status axis for a workflow — the *manual* lifecycle control, distinct
 * from approval ({@link WorkflowDefinitionStatus}: "is the test testing the right
 * thing?") and reliability ({@link WorkflowHealth}: computed from run history).
 * This axis answers "should this flow run right now, and if not, why not?".
 *
 * - `active`             — normal state; the flow participates in suites when eligible
 * - `paused`             — manually taken out of suite runs (temporary, expected to resume)
 * - `blocked`            — cannot meaningfully run/pass (e.g. waiting on a bug fix or ticket)
 * - `under_construction` — being actively built/reworked; not ready to run
 * - `deprecated`         — kept for history but no longer maintained or run
 *
 * Every value other than `active` excludes the flow from suite runs (via the
 * `paused_at` mirror — see the column doc in the DB schema). Each transition is
 * recorded as an auditable `workflow_events` row (actor, reason, linked tickets).
 *
 * Note: "has a regression" is deliberately NOT a management status — it is derivable
 * from {@link WorkflowHealth} (`failing`/`degraded` on a previously-passing flow).
 */
export const WORKFLOW_MANAGEMENT_STATUSES = [
  'active',
  'paused',
  'blocked',
  'under_construction',
  'deprecated',
] as const;

export type WorkflowManagementStatus = (typeof WORKFLOW_MANAGEMENT_STATUSES)[number];

/**
 * Approval-axis states for a workflow definition. This axis answers "is the test
 * testing the right thing?" — suite inclusion is derived {@link isWorkflowEligible eligibility}
 * (approved + ever-passed), and reliability lives in {@link WorkflowHealth}.
 *
 * User-settable via the API/CLI: `draft`, `approved`
 * (see {@link USER_SETTABLE_WORKFLOW_STATUSES}).
 *
 * Lifecycle-internal (set by the system, not by users):
 * - `generating` — the workflow is being built from a recording/import
 * - `building`   — a builder validation run is in progress
 * - `diagnostic` — builder validation failed; the workflow is parked for inspection
 * - `deleted`    — soft-deleted
 *
 */
export const WORKFLOW_STATUSES = [
  'draft',
  'generating',
  'building',
  'deleted',
  'diagnostic',
  'approved',
] as const;

export type WorkflowDefinitionStatus = (typeof WORKFLOW_STATUSES)[number];

/**
 * Statuses a user (or agent) can set directly via the API or CLI.
 *
 * - `draft`    — not yet approved; a human has not signed off on the spec
 * - `approved` — a human verified the test checks the right thing
 *
 * Approval alone does NOT put a flow into suites — a flow becomes suite-runnable
 * ({@link isWorkflowEligible eligible}) once it is approved AND has passed at least
 * once. Reliability (reliable, flaky, degraded) is the {@link WorkflowHealth}
 * dimension, computed automatically — not user-set here.
 */
export const USER_SETTABLE_WORKFLOW_STATUSES = [
  'draft',
  'approved',
] as const satisfies readonly WorkflowDefinitionStatus[];

export type UserSettableWorkflowStatus = (typeof USER_SETTABLE_WORKFLOW_STATUSES)[number];

/**
 * Every value of the `workflow_flow_type` pg enum. `workflow-flow-types.test.ts`
 * in `packages/db` pins the two together.
 *
 * - `standard` — the default; one user journey end to end
 * - `setup`    — a prerequisite flow hoisted ahead of the flows that reference it
 * - `seed`     — provisions test data and exports it as `$var.{dataKey}`
 * - `teardown` — cleans up after the seed flow that owns it
 *
 * Note this is the FULL enum, not a settable subset — unlike
 * {@link USER_SETTABLE_WORKFLOW_STATUSES}, which deliberately excludes
 * lifecycle-internal statuses. Every flow type is user-settable today. A gate
 * that genuinely needs a narrower set should name its own list and say why,
 * rather than trimming this one.
 *
 * Prefer {@link isWorkflowFlowType} over widening this tuple at a call site.
 */
export const WORKFLOW_FLOW_TYPES = ['standard', 'setup', 'seed', 'teardown'] as const;

export type WorkflowFlowType = (typeof WORKFLOW_FLOW_TYPES)[number];

/** Narrowing membership check for {@link WORKFLOW_FLOW_TYPES}. */
export function isWorkflowFlowType(value: unknown): value is (typeof WORKFLOW_FLOW_TYPES)[number] {
  return (
    typeof value === 'string' && (WORKFLOW_FLOW_TYPES as readonly string[]).includes(value)
  );
}

/**
 * Suite inclusion is a **derived, deterministic gate** called *eligibility* — there
 * is no manual "promote" action and no stored eligibility column. A flow is eligible
 * to run in suites, tags, and "run all eligible" once it is both:
 *
 * - **approved** (`status === 'approved'`) — a human signed off on the spec, and
 * - **has passed at least once** (`firstPassedAt` is set) — it worked in reality.
 *
 * Eligibility is a one-time gate: `firstPassedAt` is write-once and approval is
 * sticky, so once eligible a flow stays eligible. Two orthogonal exclusions layer
 * on top at run time: **paused** (`pausedAt` set — mirrored from the
 * {@link WorkflowManagementStatus management status}: any non-`active` status stamps
 * it) and **degraded** ({@link WorkflowHealth}) both keep an otherwise-eligible flow
 * out of suite runs.
 *
 * Single-sourced here so the API, repos, and the web app derive eligibility the
 * same way instead of re-implementing the `approved && everPassed` check.
 */
export function isWorkflowEligible(wf: {
  status: string;
  firstPassedAt?: Date | string | null;
}): boolean {
  return wf.status === 'approved' && wf.firstPassedAt != null;
}

/**
 * Legacy suite-inclusion axis (the *promotion model*), used when an org has NOT been
 * moved to the eligibility model (per-org `workflow.eligibility` flag). Stored on a
 * nullable column: `null` = never promoted, `'promoted'` = runs in suites, `'paused'`
 * = explicitly excluded. Removed once the flag graduates and the eligibility model is
 * the only model.
 */
export const WORKFLOW_PROMOTIONS = ['promoted', 'paused'] as const;

export type WorkflowPromotion = (typeof WORKFLOW_PROMOTIONS)[number];

/** The promotion column's full value space: `null` = never promoted (the default). */
export type WorkflowPromotionState = WorkflowPromotion | null;

/** Context provided by the agent when a workflow step fails */
export type WorkflowFailureContext = {
  /** What the agent observed happening when the step failed */
  observedBehavior: string;
  /** What was expected to happen */
  expectedBehavior: string;
  /** Steps to reproduce the failure */
  reproSteps: string;
  /** Specific questions the agent poses to help improve instructions (debug mode only) */
  questions?: string[];
};

export type WorkspaceRedactionReason = 'secret' | 'binary' | 'large' | 'system';

export type WorkspaceManifestEntry = {
  path: string;
  type: 'file' | 'dir';
  size?: number;
  mtime?: string;
  language?: string;
  redacted?: boolean;
  redactionReason?: WorkspaceRedactionReason;
};

export type WorkspaceManifest = {
  version: string;
  root: string;
  generatedAt: string;
  entries: WorkspaceManifestEntry[];
};

export type WorkspaceStreamEvent =
  | {
      type: 'workspace-manifest';
      data: WorkspaceManifest;
    }
  | {
      type: 'workspace-file-created';
      data: WorkspaceManifestEntry;
    }
  | {
      type: 'workspace-file-updated';
      data: {
        path: string;
        size?: number;
        mtime?: string;
        version?: string;
      };
    }
  | {
      type: 'workspace-file-deleted';
      data: { path: string };
    }
  | {
      type: 'workspace-file-content';
      data: {
        path: string;
        content: string;
        encoding?: 'utf-8';
        version?: string;
      };
    };

export type WorkflowTestRunSummary = {
  id: string;
  workflowId: string;
  status: WorkflowTestStatus;
  snapshotVersion: number;
  /** 1-based retry attempt number; see `workflow_test_runs.attempt`. */
  attempt?: number;
  errorMessage: string | null;
  linkedIssueId: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
};

export type WorkflowTestSuiteStatus = 'queued' | 'running' | 'completed';

export type WorkflowTestEvent = {
  type: 'workflow-test';
  suiteId: string;
  emittedAt: string;
  workflowId: string;
  testRunId: string;
  workflowRunId: string | null;
  status: WorkflowTestStatus;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  errorMessage?: string | null;
  skipReason?: string | null;
  linkedIssueId?: string | null;
  message?: string;
  /** The workflow's reliability dimension at emit time (drives the UI health badge). */
  health?: WorkflowHealth;
  // Wait/segment tracking
  resumeAt?: string | null;
  segmentIndex?: number | null;
  totalSegments?: number | null;
  /** Monotonic sequence number for ordering concurrent events */
  _seq?: number;
};

export type WorkflowTestSuiteEvent = {
  type: 'workflow-test-suite';
  suiteId: string;
  emittedAt: string;
  status: WorkflowTestSuiteStatus;
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  totalWorkflows: number;
  completedWorkflows: number;
  failedWorkflows: number;
  inconclusiveWorkflows: number;
  successfulWorkflows: number;
  skippedWorkflows: number;
  waitingWorkflows: number;
  /** @deprecated Quarantine removed; always 0. Drop with the column in a follow-up. */
  quarantinedWorkflows: number;
  /** Workflows skipped because their health is `degraded` (shown "degraded — not run"). */
  degradedWorkflows: number;
  seedTotal: number;
  seedSucceeded: number;
  seedFailed: number;
  teardownTotal: number;
  teardownSucceeded: number;
  teardownFailed: number;
  errorMessage?: string | null;
  message?: string;
  /** Monotonic sequence number for ordering concurrent events */
  _seq?: number;
};

export type WorkflowTestStreamEvent = WorkflowTestEvent | WorkflowTestSuiteEvent;
